import * as React from 'react';
import CategorySearchSuggestion from '../components/suggestions/categorySearchSuggestion/CategorySearchSuggestion';
import AttachmentFileSearchSuggestion from '../components/suggestions/fileSearchSuggestions/AttachmentFileSearchSuggestion';
import OdspFileSearchSuggestion from '../components/suggestions/fileSearchSuggestions/OdspFileSearchSuggestion';
import MessageSearchSuggestion from '../components/suggestions/messageSearchSuggestion/MessageSearchSuggestion';
import TextSearchSuggestion from '../components/suggestions/textSearchSuggestion/TextSearchSuggestion';
import EventSearchSuggestion from '../components/suggestions/eventSearchSuggestion/EventSearchSuggestion';
import TrySearchSuggestion from '../components/suggestions/TrySearchSuggestion';
import WebSearchSuggestion from '../components/suggestions/webSearchSuggestion/WebSearchSuggestion';
import SettingSearchSuggestion from '../components/suggestions/SettingSearchSuggestion';
import {
    lazyLogLocalContentLayout,
    LocalContentLayout,
    LocalContentLayoutGroup,
    Suggestion,
    SuggestionKind,
    SuggestionSet,
    AttachmentFileSuggestion,
    OdspFileSuggestion,
} from 'owa-search-service';
import { lazyLogClientLayout, ResultsView, ItemType } from 'owa-search-instrumentation';
import { PeopleSearchSuggestion } from '../components/suggestions/peopleSearchSuggestion/PeopleSearchSuggestion';
import { SearchScenarioId } from '../store/schema/SearchScenarioId';
import getSubstrateSearchScenarioBySearchScenarioId from './getSubstrateSearchScenarioBySearchScenarioId';
import {
    suggestionsHeader,
    potentialMatchesHeader,
    trySuggestionsHeader,
    recentFilesSuggestionsHeader,
} from '../components/suggestions/searchSuggestion/SearchSuggestion.locstring.json';
import loc from 'owa-localize';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import getScenarioStore from '../selectors/getScenarioStore';

const styles = require('../components/suggestions/searchSuggestion/SearchSuggestion.scss');

/**
 * Build out the suggestions content.
 * Importantly: the order here needs to match the order that we set in the store in orderSuggestions!
 */
export default function getSuggestionCalloutContent(
    suggestionSet: SuggestionSet,
    scenarioId: SearchScenarioId,
    focusSearchInput: () => void
): JSX.Element[] {
    if (!suggestionSet) {
        return [];
    }

    /**
     * Cache existing items (if new suggestions are still in progress) to be
     * returned if new suggestions aren't ready to be displayed.
     */
    const items: JSX.Element[] = [];
    const localContentLayoutGroups: { [groupType: string]: LocalContentLayout[] } = {};

    const allSuggestions = suggestionSet.Suggestions;
    const traceId = suggestionSet.TraceId;

    const scenarioStore = getScenarioStore(scenarioId);

    const query = scenarioStore.searchTextForSuggestion;

    const searchExecutingSuggestions = getSearchExecutingSuggestionGroup(allSuggestions);
    const potentialMatchSuggestions = getPotentialMatchesSuggestionGroup(allSuggestions);
    const trySuggestionGroup = getTrySuggestionGroup(allSuggestions);
    const webSearchSuggestion = getWebSearchSuggestion(allSuggestions);
    const odspFileSuggestions = getOdspFileSuggestionGroup(allSuggestions);

    const shouldShowOdspFileSuggestions = !query && odspFileSuggestions.length > 0;

    // Try out suggestions in zero query suggestion callout
    if (trySuggestionGroup.length > 0) {
        items.push(<div className={styles.header}>{loc(trySuggestionsHeader)}</div>);

        trySuggestionGroup.map((suggestion: Suggestion, index: number) => {
            addSuggestionComponent(
                items,
                suggestion,
                index,
                scenarioId,
                traceId,
                localContentLayoutGroups
            );
        });
    }

    // Suggestions that go under the "Suggested Searches" header (i.e. suggestions that execute a search)
    if (searchExecutingSuggestions.length > 0) {
        items.push(
            <div
                className={styles.header}
                onClick={instrumentHeaderClick(true /* isSuggestedSearchesHeader */)}>
                {loc(suggestionsHeader)}
            </div>
        );

        searchExecutingSuggestions.map((suggestion: Suggestion, index: number) => {
            // Need to account for first group of try suggestions.
            const adjustedIndex = trySuggestionGroup.length + index;
            addSuggestionComponent(
                items,
                suggestion,
                adjustedIndex,
                scenarioId,
                traceId,
                localContentLayoutGroups
            );
        });
    }

    // Add horizontal separator if we have 2 groups
    if (searchExecutingSuggestions.length > 0 && potentialMatchSuggestions.length > 0) {
        items.push(<hr className={styles.divider} />);
    }

    // Suggestions that go under the "Potential matches" header (i.e. suggestions that are actually search results)
    if (potentialMatchSuggestions.length > 0) {
        items.push(
            <div
                className={styles.header}
                onClick={instrumentHeaderClick(false /* isSuggestedSearchesHeader */)}>
                {loc(potentialMatchesHeader)}
            </div>
        );

        potentialMatchSuggestions.map((suggestion: Suggestion, index: number) => {
            // Need to account for first group of suggestions.
            const adjustedIndex =
                trySuggestionGroup.length + searchExecutingSuggestions.length + index;

            addSuggestionComponent(
                items,
                suggestion,
                adjustedIndex,
                scenarioId,
                traceId,
                localContentLayoutGroups
            );
        });
    }

    if (shouldShowOdspFileSuggestions) {
        items.push(
            <div
                className={styles.header}
                onClick={instrumentHeaderClick(false /* isSuggestedSearchesHeader */)}>
                {loc(recentFilesSuggestionsHeader)}
            </div>
        );

        odspFileSuggestions.map((suggestion: Suggestion, index: number) => {
            // Need to account for other groups of suggestions.
            const adjustedIndex =
                trySuggestionGroup.length +
                searchExecutingSuggestions.length +
                potentialMatchSuggestions.length +
                index;

            addSuggestionComponent(
                items,
                suggestion,
                adjustedIndex,
                scenarioId,
                traceId,
                localContentLayoutGroups,
                focusSearchInput
            );
        });
    }

    // Lone suggestion that is stylistically its own group, even though it has no header
    if (webSearchSuggestion.length > 0) {
        // Add another horizontal separator if we have 2+ groups
        if (
            searchExecutingSuggestions.length > 0 ||
            potentialMatchSuggestions.length > 0 ||
            shouldShowOdspFileSuggestions
        ) {
            items.push(<hr className={styles.divider} />);
        }

        // Need to account for any previous groups and their suggestions
        const adjustedIndex =
            trySuggestionGroup.length +
            searchExecutingSuggestions.length +
            potentialMatchSuggestions.length +
            (shouldShowOdspFileSuggestions ? odspFileSuggestions.length : 0);

        addSuggestionComponent(
            items,
            webSearchSuggestion[0],
            adjustedIndex,
            scenarioId,
            traceId,
            localContentLayoutGroups,
            focusSearchInput
        );
    }

    if (isFeatureEnabled('sea-instv2')) {
        if (suggestionSet.IsComplete) {
            const logicalId = scenarioStore.traceIdToLogicalIdMap.get(traceId);

            const resultsView = getResultsView(suggestionSet.Suggestions, traceId);
            lazyLogClientLayout.importAndExecute(
                getSubstrateSearchScenarioBySearchScenarioId(scenarioId),
                null /* Puid */,
                null /* TenantId */,
                logicalId,
                traceId,
                'Vertical',
                resultsView
            );
        }
    }
    if (suggestionSet.IsComplete && Object.keys(localContentLayoutGroups).length > 0) {
        lazyLogLocalContentLayout.importAndExecute(
            traceId,
            localContentLayoutGroups,
            getSubstrateSearchScenarioBySearchScenarioId(scenarioId)
        );
    }

    return items;
}

const getInstrumentationItemType = (suggestionKind: SuggestionKind): ItemType => {
    switch (suggestionKind) {
        case SuggestionKind.Category:
            return 'Category';
        case SuggestionKind.TrySuggestion:
            return 'Try';
        case SuggestionKind.WebSearch:
            return 'Web';
        case SuggestionKind.Keywords:
            return 'TextualSuggestion';
        case SuggestionKind.People:
            return 'PeopleSuggestion';
        case SuggestionKind.AttachmentFile:
        case SuggestionKind.OdspFile:
            return 'FileSuggestion';
        case SuggestionKind.Event:
            return 'EventSuggestion';
        case SuggestionKind.Message:
            return 'MessageSuggestion';
        case SuggestionKind.Setting:
            return 'Setting';
        default:
            return 'Unknown';
    }
};

const getResultsView = (suggestions: Suggestion[], traceId: string) => {
    const resultsView: ResultsView[] = [];

    suggestions.forEach((suggestion, index) => {
        if (suggestion) {
            const itemType = getInstrumentationItemType(suggestion.kind);

            resultsView.push({
                type: 'Entity',
                name: 'Suggestions',
                referenceId: suggestion.ReferenceId,
                providerId: traceId,
                position: index + 1, // position starts from 1
                entityType: itemType,
                placementType: 'Visible',
            });
        }
    });

    const groupResultsView: ResultsView[] = [];
    groupResultsView.push({
        layoutType: 'Vertical',
        type: 'Group',
        name: 'Suggestions',
        position: 1,
        placementType: 'Visible',
        resultsView: resultsView,
    });

    return groupResultsView;
};

const getSearchExecutingSuggestionGroup = (suggestions: Suggestion[]): Suggestion[] => {
    return suggestions.filter((suggestion: Suggestion) => {
        const suggestionKind = suggestion.kind;

        switch (suggestionKind) {
            case SuggestionKind.Keywords:
            case SuggestionKind.People:
            case SuggestionKind.PrivateDistributionList:
            case SuggestionKind.Category:
                return true;
            default:
                return false;
        }
    });
};

const getTrySuggestionGroup = (suggestions: Suggestion[]): Suggestion[] => {
    return suggestions.filter((suggestion: Suggestion) => {
        const suggestionKind = suggestion.kind;
        return suggestionKind === SuggestionKind.TrySuggestion;
    });
};

const getOdspFileSuggestionGroup = (suggestions: Suggestion[]): Suggestion[] => {
    return suggestions.filter((suggestion: Suggestion) => {
        return suggestion.kind === SuggestionKind.OdspFile;
    });
};

const getPotentialMatchesSuggestionGroup = (suggestions: Suggestion[]): Suggestion[] => {
    return suggestions.filter((suggestion: Suggestion) => {
        const suggestionKind = suggestion.kind;

        switch (suggestionKind) {
            case SuggestionKind.AttachmentFile:
            case SuggestionKind.Message:
            case SuggestionKind.Event:
            case SuggestionKind.Setting:
                return true;
            default:
                return false;
        }
    });
};

const getWebSearchSuggestion = (suggestions: Suggestion[]): Suggestion[] =>
    suggestions.filter((suggestion: Suggestion) => suggestion.kind === SuggestionKind.WebSearch);

const addSuggestionComponent = (
    items: JSX.Element[],
    suggestion: Suggestion,
    index: number,
    scenarioId: SearchScenarioId,
    traceId: string,
    localContentLayoutGroups: { [groupType: string]: LocalContentLayout[] },
    focusSearchInput?: () => void
) => {
    // Subset of SearchSuggestion props that all suggestions share.
    const sharedProps = {
        index: index,
        scenarioId: scenarioId,
        suggestionSetTraceId: traceId,
    };

    // Add the appropriate component based on suggestion kind.
    switch (suggestion.kind) {
        case SuggestionKind.People:
            items.push(<PeopleSearchSuggestion {...sharedProps} suggestion={suggestion} />);

            if (suggestion.Source === 'gal') {
                const galGroupType: LocalContentLayoutGroup = 'gal';
                if (!localContentLayoutGroups[galGroupType]) {
                    localContentLayoutGroups[galGroupType] = [];
                }

                // add to local content layout
                localContentLayoutGroups[galGroupType].push({
                    entityId: suggestion.ReferenceId,
                    position: index + 1,
                });
            }
            break;
        case SuggestionKind.Keywords:
            items.push(<TextSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            break;
        case SuggestionKind.Message:
            items.push(<MessageSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            break;
        case SuggestionKind.Category:
            items.push(<CategorySearchSuggestion {...sharedProps} suggestion={suggestion} />);

            const categoryGroupType: LocalContentLayoutGroup = 'category';
            if (!localContentLayoutGroups[categoryGroupType]) {
                localContentLayoutGroups[categoryGroupType] = [];
            }

            // add to local content layout
            localContentLayoutGroups[categoryGroupType].push({
                entityId: suggestion.ReferenceId,
                position: index + 1,
            });
            break;
        case SuggestionKind.AttachmentFile:
            items.push(
                <AttachmentFileSearchSuggestion
                    {...sharedProps}
                    suggestion={suggestion as AttachmentFileSuggestion}
                />
            );
            break;
        case SuggestionKind.OdspFile:
            items.push(
                <OdspFileSearchSuggestion
                    {...sharedProps}
                    suggestion={suggestion as OdspFileSuggestion}
                    focusSearchInput={focusSearchInput}
                />
            );
            break;
        case SuggestionKind.Event:
            items.push(<EventSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            break;
        case SuggestionKind.TrySuggestion:
            items.push(<TrySearchSuggestion {...sharedProps} suggestion={suggestion} />);

            const tryGroupType: LocalContentLayoutGroup = 'try';
            if (!localContentLayoutGroups[tryGroupType]) {
                localContentLayoutGroups[tryGroupType] = [];
            }

            // add to local content layout
            localContentLayoutGroups[tryGroupType].push({
                entityId: suggestion.ReferenceId,
                position: index + 1,
            });
            break;
        case SuggestionKind.WebSearch:
            const webSearchGroupType: LocalContentLayoutGroup = 'web';
            if (!localContentLayoutGroups[webSearchGroupType]) {
                localContentLayoutGroups[webSearchGroupType] = [];
            }

            // add to local content layout
            localContentLayoutGroups[webSearchGroupType].push({
                entityId: suggestion.ReferenceId,
                position: index + 1,
            });
            items.push(
                <WebSearchSuggestion
                    {...sharedProps}
                    suggestion={suggestion}
                    focusSearchInput={focusSearchInput}
                />
            );
            break;
        case SuggestionKind.Setting:
            const settingSearchGroupType: LocalContentLayoutGroup = 'setting';
            if (!localContentLayoutGroups[settingSearchGroupType]) {
                localContentLayoutGroups[settingSearchGroupType] = [];
            }

            // add to local content layout
            localContentLayoutGroups[settingSearchGroupType].push({
                entityId: suggestion.ReferenceId,
                position: index + 1,
            });
            items.push(<SettingSearchSuggestion {...sharedProps} suggestion={suggestion} />);
            break;
    }
};

const instrumentHeaderClick = (isSuggestedSearchesHeader: boolean) => {
    return () => {
        logUsage('Search_SuggestionHeaderClicked', [isSuggestedSearchesHeader]);
    };
};
